import { assetUrl } from '../api/assets';
import { useEffect, useRef, useState } from 'react';
import { useWallet } from '../api/wallet';
import { useActiveEvents } from '../api/events';
import { useOnline } from '../pwa/useOnline';
import { ShellGlyph } from './glyphs';
import { currencyLabel, eventCurrencyCode } from '../lib/currency';

// jsdom (this project's pinned test environment) has no `matchMedia` at all by default, hence the
// optional chaining all the way down — same idiom `MapPanZoom.tsx`/`WorldScreen.tsx` already use.
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

// The rAF count-up is a sanctioned exception to "CSS transitions only" (docs/DESIGN.md §10's
// justified-requestAnimationFrame carve-out, already covering the minigame loop and the battle
// ATB lerp) — a CSS `transition` cannot animate the actual digits of a number, only its opacity/
// transform, and this needs the intermediate INTEGERS themselves to read as a real count.
const COUNT_UP_DURATION_MS = 500;

/**
 * Animates `target` from its own previous value with an eased rAF count-up (docs/DESIGN.md §21),
 * except:
 *  - the FIRST time a real value ever arrives (`hasValue.current` still `false` — i.e. the
 *    pending→loaded transition on initial mount), which settles instantly. Animating that one
 *    would count up from a meaningless placeholder every time the HUD mounts; the count-up is a
 *    reward beat for a balance CHANGE (a purchase, a forage reward), not a mount effect.
 *  - reduced motion, which also settles instantly (§11.3: no roll, the digits show the new value
 *    right away — the change is still visible, just not animated).
 *  - a genuine no-op change (`target` already equals what's on screen — a refetch returning the
 *    same balance, or a target that happens to land back where a still-mid-flight animation
 *    already sits), which settles instantly with no new bump.
 *
 * Review round 2 (finding 2): every "from" reads the value ACTUALLY ON SCREEN right now
 * (`shownRef.current`, kept in sync by every `commit` call), never a bookmark of the
 * previous target. The earlier version assigned that bookmark the instant an animation STARTED,
 * not when it finished — so a second balance change landing inside the first's half-second window would
 * animate from the first change's own unreached target rather than from the number actually
 * showing, and the digits would visibly snap forward to that stale target before reversing toward
 * the real one. Reading the live `shown` ref instead means an interrupted animation's replacement
 * always continues smoothly from wherever the interrupted one had actually gotten to.
 *
 * `bumpTick` increments once per genuine change (whether or not it animates) so a caller can key
 * a decorative wrapper on it and get a fresh element — and therefore a replayed CSS animation —
 * every time, including under reduced motion where the JS count-up itself never runs (the
 * `.hud-currency-bump` rule's own reduced-motion override is what turns that into a no-op, per
 * §11.3 "replace movement with cross-fades, do not simply disable" — the bump is pure decoration
 * though, unlike the digits/flyer, so simply disabling it is fine).
 */
function useCountUp(target: number | undefined): { shown: number; bumpTick: number } {
  const [shown, setShownState] = useState(target ?? 0);
  const [bumpTick, setBumpTick] = useState(0);
  const hasValue = useRef(false);
  const shownRef = useRef(shown);

  const commit = (value: number) => {
    shownRef.current = value;
    setShownState(value);
  };

  useEffect(() => {
    if (target === undefined) return;
    if (!hasValue.current) {
      hasValue.current = true;
      commit(target);
      return;
    }
    if (shownRef.current === target) return; // already showing it — nothing to animate
    const from = shownRef.current;
    setBumpTick((n) => n + 1);
    if (prefersReducedMotion()) {
      commit(target);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / COUNT_UP_DURATION_MS);
      const eased = 1 - Math.pow(1 - p, 3);
      commit(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return { shown, bumpTick };
}

/**
 * Persistent Shells balance (task-15-brief.md), rendered in AppShell between the offline banner
 * and the content region — on every screen inside the shell EXCEPT the wardrobe's own full-screen
 * modal route, which mounts outside <AppShell> entirely (routes.tsx), so no route-based hiding
 * logic is needed here.
 *
 * `role="status"`/`aria-live="polite"` on the currency span (not the whole `<header>`) so a
 * balance change — a purchase, a forage reward — is announced without requiring focus to move,
 * same idiom ReconnectingBanner already uses for connectivity. `.sr-only` (this codebase's one
 * visually-hidden idiom, see base.css's own comment on it) carries the "Shells" label for screen
 * readers, since the glyph + bare number alone would announce as just a number.
 *
 * Dimmed (`.hud-offline`), not hidden, while offline — §11.11 "never blank, always honest": the
 * last-known balance stays visible and legible, just visually deprioritised, rather than
 * disappearing or looking untouched while stale.
 *
 * Fix round 1 (review finding): a genuine fetch error (still online — a 500, a bad response)
 * used to fall through `data?.shells ?? 0`, i.e. silently render "0", which reads as a real
 * balance rather than the unknown-value state it actually is — the same §11.11 dishonesty the
 * offline-dimming comment above explicitly guards against for connectivity. `isError` now renders
 * a quiet "—" placeholder instead (never blank, never a fabricated number), dimmed the same way
 * offline is via `.hud-error` (shares `.hud-offline`'s look, kept as its own class since the two
 * conditions are independent — a request can fail while online, and vice versa nothing fails
 * while genuinely offline since the fetch never completes either way).
 *
 * Phase 7 Task 13 widening: a second, additional chip for a liveops event's own currency (today,
 * only ever `lantern_sparks`) — Shells stays primary and always renders; this is deliberately ONE
 * extra chip, not a general N-currency HUD framework (`lib/currency.ts`'s own `eventCurrencyCode`
 * doc comment). Shown only when BOTH hold: at least one event is currently active
 * (`useActiveEvents()` — never futures, per that endpoint's own window gate) AND the balance for
 * that currency is genuinely positive — a zero balance stays hidden rather than showing an empty
 * "0 Lantern Sparks" chip the moment the window opens, before the player has earned a single one
 * (SCREENS.md's own "never an empty shell" ribbon reasoning, applied here too). No active event
 * hides the chip outright even with a leftover positive balance from a past event — the whole
 * point of an EVENT currency is that its own HUD surface only exists while its event does.
 */
export function ShellsHud() {
  const { data, isPending, isError } = useWallet();
  const events = useActiveEvents();
  const isOnline = useOnline();

  const hasActiveEvent = (events.data?.length ?? 0) > 0;
  const sparksCode = hasActiveEvent ? eventCurrencyCode(data) : null;
  const sparksBalance = sparksCode ? data?.[sparksCode] ?? 0 : 0;
  const showSparksChip = !isPending && !isError && sparksCode != null && sparksBalance > 0;

  const settled = data?.shells ?? 0;
  const { shown: rolling, bumpTick } = useCountUp(isPending || isError ? undefined : settled);

  return (
    <header className={`hud${isOnline ? '' : ' hud-offline'}${isError ? ' hud-error' : ''}`}>
      <span className="hud-currency" role="status" aria-live="polite">
        <ShellGlyph />
        {isPending ? (
          <>
            <span className="skeleton skeleton-chip" aria-hidden="true" />
            <span className="sr-only">Shells</span>
          </>
        ) : isError ? (
          <>
            <span className="hud-amount">—</span>
            <span className="sr-only">Shells</span>
          </>
        ) : (
          // key={bumpTick}: a fresh element per genuine balance change, so the always-present
          // `.hud-currency-bump` class replays its CSS animation each time — a class toggle alone
          // would not restart an already-finished animation. Reduced motion still gets this key
          // bump (and therefore the class), it's just `.hud-currency-bump { animation: none; }`
          // there (base.css, docs/DESIGN.md §21) that makes it inert.
          <span className="hud-currency-bump" key={bumpTick}>
            <span className="hud-amount" data-testid="hud-shells-digits" aria-hidden="true">{rolling}</span>
            <span className="sr-only" data-testid="hud-shells-announced">{settled} Shells</span>
          </span>
        )}
      </span>
      {showSparksChip && (
        // An icon + the number, like Shells — the name printed inside a rail-wide chip wrapped onto
        // three lines ("200 / Lantern / Sparks") and, on a landscape phone, pushed the rail's Bag
        // and Me tabs past the fold (the wide-screen review). The name stays for screen readers
        // and as the hover title.
        <span className="hud-currency hud-currency-event" role="status" aria-live="polite" title={currencyLabel(sparksCode)}>
          <img className="hud-currency-icon" src={assetUrl('ui_decor_lantern')} alt="" width={20} height={20} />
          <span className="hud-amount">{sparksBalance}</span>
          <span className="sr-only">{currencyLabel(sparksCode)}</span>
        </span>
      )}
    </header>
  );
}
